// 详情页的公共零件：页框（返回 / 标题 / 操作）、面板、键值、指标条、时间线。
// 发货批次详情与异常详情共用；规格与订单详情 OrderDetail 一致。详情内部按容器宽度排版（container query）。
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { Icon } from '@/components/Icon';
import { Skeleton } from '@/components/States';
import { legacyHref } from '@/shell/nav';
import s from './DetailParts.module.css';

export function DetailFrame({ embedded, compactSub, fallback, title, phoneTitle, phoneActions, tags, actions, sub, children }: {
  embedded?: boolean;
  /** Wide detail screens place the subtitle in the heading block, as in the design source. */
  compactSub?: boolean;
  /** 直接打开详情（没有上一页）时，返回键回到这里 */
  fallback: string;
  title: ReactNode;
  phoneTitle?: ReactNode;
  phoneActions?: ReactNode;
  tags?: ReactNode;
  actions?: ReactNode;
  sub?: ReactNode;
  children: ReactNode;
}) {
  const ff = useFormFactor();
  const nav = useNavigate();
  // 手机：深层页没有 Tab 栏，操作放底部操作条（44 高），导航栏只留返回 + 标题 + 主状态
  const foot = ff === 'phone' && !embedded;
  const customPhoneHeader = foot && phoneTitle !== undefined;
  return (
    <div className={[s.page, embedded && s.embedded, compactSub && s.compactSub, ff === 'phone' && s.phone].filter(Boolean).join(' ')}>
      <header className={s.head}>
        {!embedded && (
          <button className={s.back} onClick={() => (history.length > 1 ? nav(-1) : nav(fallback))} aria-label="返回">
            <Icon name="back" size={20} />
          </button>
        )}
        {customPhoneHeader ? <>
          <h1 className={s.phoneTitle}>{phoneTitle}</h1>
          <span className={s.phoneActions}>{phoneActions}</span>
        </> : <>
          <h1>{title}</h1>
          {tags && <span className={s.tags}>{tags}</span>}
          <span className={s.sp} />
          {!foot && actions}
        </>}
      </header>
      <div className={s.scroll}>
        {sub && <p className={s.sub}>{sub}</p>}
        {children}
      </div>
      {foot && actions && <footer className={s.foot}>{actions}</footer>}
    </div>
  );
}

/** 宽：主区 + 右侧 300 信息栏；窄（手机、分栏内嵌）：单列，信息栏排到最后 */
export function Cols({ wide, main, side }: { wide: boolean; main: ReactNode; side: ReactNode }) {
  if (!wide) return <div className={s.col}>{main}{side}</div>;
  return (
    <div className={s.grid}>
      <div className={s.col}>{main}</div>
      <aside className={s.col}>{side}</aside>
    </div>
  );
}

export function Panel({ title, extra, children, flush }: { title: ReactNode; extra?: ReactNode; children: ReactNode; flush?: boolean }) {
  return (
    <section className={s.panel}>
      <h3>{title}{extra && <span className={s.extra}>{extra}</span>}</h3>
      <div className={flush ? s.flush : s.body}>{children}</div>
    </section>
  );
}

/** 「标签 / 值」：值为 undefined 的行不出（无权字段被服务端裁掉时整行不显示） */
export function KV({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className={s.kv}>
      {rows.filter(([, v]) => v !== undefined && v !== null && v !== false).map(([k, v]) => (
        <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
      ))}
    </dl>
  );
}

export type Kpi = { label: string; value: ReactNode; unit?: string; tone?: 'hl' | 'warn' | 'bad' };

export function Kpis({ items }: { items: Kpi[] }) {
  return (
    <div className={s.kpis} style={{ ['--n' as string]: items.length }}>
      {items.map((k) => (
        <div key={k.label}>
          <span>{k.label}</span>
          <b className={`tnum ${k.tone ? s[k.tone] : ''}`}>{k.value}{k.unit && <small> {k.unit}</small>}</b>
        </div>
      ))}
    </div>
  );
}

export type TimelineItem = { key: string; title: ReactNode; time?: string; body?: ReactNode; tone?: 'hl' | 'ok' | 'bad' };

/** 时间线（文档 18 §7.3）：默认灰 / hl 蓝（当前）/ ok 绿（完成）/ bad 红 */
export function Timeline({ items }: { items: TimelineItem[] }) {
  return (
    <ul className={s.tl}>
      {items.map((it) => (
        <li key={it.key} className={it.tone ? s[it.tone] : undefined}>
          <div className={s.tlh}><b>{it.title}</b><time className="tnum">{it.time || '—'}</time></div>
          {it.body && <div className={s.tlb}>{it.body}</div>}
        </li>
      ))}
    </ul>
  );
}

/** 只读体验期：所有写操作都去老版同一页面 */
export function LegacyButton({ path, children = '在老版中操作' }: { path: string; children?: ReactNode }) {
  return <a className={s.legacy} href={legacyHref(path)}>{children}</a>;
}

export function Loading() {
  return (
    <div className={s.loading} aria-busy="true">
      <Skeleton />
      <Skeleton lines={6} />
    </div>
  );
}
